import React from 'react';
import { Building2, Target, Cpu, Eye, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AboutModal: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="text-slate-300 space-y-6">
      {/* Introduction */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl">
          <Building2 className="w-10 h-10 text-white" />
        </div>
        <p className="text-lg leading-relaxed">
          {t('footer.modals.about.introduction')}
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-6">
        <div className="bg-slate-700/30 rounded-2xl p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Target className="w-6 h-6 mr-3 text-indigo-400" />
            {t('footer.modals.about.genesis.title')}
          </h3>
          <p className="leading-relaxed">
            {t('footer.modals.about.genesis.description')}
          </p>
        </div>

        <div className="bg-slate-700/30 rounded-2xl p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <BarChart3 className="w-6 h-6 mr-3 text-emerald-400" />
            {t('footer.modals.about.approach.title')}
          </h3>
          <p className="leading-relaxed">
            {t('footer.modals.about.approach.description')}
          </p>
        </div>

        <div className="bg-slate-700/30 rounded-2xl p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Cpu className="w-6 h-6 mr-3 text-blue-400" />
            {t('footer.modals.about.technology.title')}
          </h3>
          <div className="space-y-3">
            {t('footer.modals.about.technology.features', { returnObjects: true }).map((feature: string, index: number) => (
              <div key={index} className="flex items-start">
                <div className="w-2 h-2 bg-blue-400 rounded-full mr-3 mt-2"></div>
                <p>{feature}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-700/30 rounded-2xl p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Eye className="w-6 h-6 mr-3 text-purple-400" />
            {t('footer.modals.about.vision.title')}
          </h3>
          <p className="leading-relaxed">
            {t('footer.modals.about.vision.description')}
          </p>
        </div>

        <div className="bg-gradient-to-br from-indigo-500/10 to-purple-600/10 border border-indigo-500/20 rounded-2xl p-6">
          <h3 className="text-xl font-semibold text-white mb-4">
            {t('footer.modals.about.stats.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent mb-2">
                {t('footer.modals.about.stats.cvs_analyzed.number')}
              </div>
              <p className="text-slate-400 text-sm">{t('footer.modals.about.stats.cvs_analyzed.label')}</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent mb-2">
                {t('footer.modals.about.stats.founded.number')}
              </div>
              <p className="text-slate-400 text-sm">{t('footer.modals.about.stats.founded.label')}</p>
            </div>
          </div>
          
          <div className="mt-6 pt-4 border-t border-slate-600/30">
            <p className="text-center text-slate-400 text-sm">
              {t('footer.modals.about.partnership.description')}
            </p>
            <p className="text-center text-indigo-400 text-sm font-medium">
              {t('footer.modals.about.partnership.website')}
            </p>
          </div>
        </div>
      </div>

      {/* Call to action */}
      <div className="bg-gradient-to-br from-emerald-500/10 to-teal-600/10 border border-emerald-500/20 rounded-2xl p-6 text-center">
        <h3 className="text-xl font-bold text-white mb-3">
          {t('footer.modals.about.cta.title')}
        </h3>
        <button 
          onClick={() => {
            // Close modal first
            const closeButton = document.querySelector('[data-modal-close]') as HTMLButtonElement;
            if (closeButton) closeButton.click();
            
            // Then redirect to registration section
            setTimeout(() => {
              const uploadSection = document.querySelector('#upload-section');
              if (uploadSection) {
                uploadSection.scrollIntoView({ behavior: 'smooth' });
              }
            }, 300);
          }}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
        >
          {t('footer.modals.about.cta.button')}
        </button>
      </div>
    </div>
  );
};

export default AboutModal;
